/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { MediaQueries } from '@/constants/media-queries'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import { useLingui } from '@lingui/react'
import { ArrowRight, Link } from 'lucide-react'
import Image from 'next/image'
import React, { useEffect, useRef, useState } from 'react'
import { timelineData } from '../marketing/marketing'
import Globe from './globe'

export interface TimelineItem {
  id: number
  title: any
  link: string
  content: any
  icon: string
  relatedIds: number[]
}

export default function RadialOrbitalTimeline() {
  const { i18n } = useLingui()
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({})
  const [viewMode] = useState<'orbital'>('orbital')
  const [rotationAngle, setRotationAngle] = useState<number>(0)
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const [centerOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [activeNodeId, setActiveNodeId] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const orbitRef = useRef<HTMLDivElement>(null)
  const nodeRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const isMobile = useMediaQuery(MediaQueries.SmDown, { ssr: true, fallback: true })

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({})
      setActiveNodeId(null)
    }
  }

  const toggleItem = (id: number) => {
    setExpandedItems((prev) => {
      const newState = { ...prev }
      Object.keys(newState).forEach((key) => {
        if (parseInt(key) !== id) newState[parseInt(key)] = false
      })
      newState[id] = !prev[id]
      if (!prev[id]) {
        setActiveNodeId(id)
        centerViewOnNode(id)
      } else {
        setActiveNodeId(null)
      }
      return newState
    })
  }

  const centerViewOnNode = (nodeId: number) => {
    if (viewMode !== 'orbital' || !nodeRefs.current[nodeId]) return
    const nodeIndex = timelineData.findIndex((item) => item.id === nodeId)
    const targetAngle = (nodeIndex / timelineData.length) * 360
    setRotationAngle(270 - targetAngle)
  }

  const calculateNodePosition = (index: number, total: number) => {
    const baseRadius =
      isMobile ?
        typeof window !== 'undefined' ?
          Math.min(140, window.innerWidth * 0.45)
        : 140
      : typeof window !== 'undefined' ? Math.min(300, window.innerWidth * 0.35)
      : 300
    const angle = ((index / total) * 360 + rotationAngle) % 360
    const radian = (angle * Math.PI) / 180
    const x = baseRadius * Math.cos(radian) + centerOffset.x
    const y = baseRadius * Math.sin(radian) + centerOffset.y
    const zIndex = Math.round(100 + 50 * Math.cos(radian))
    const opacity = Math.max(0.6, Math.min(1, 0.6 + 0.4 * ((1 + Math.sin(radian)) / 2)))
    return { x, y, angle, zIndex, opacity }
  }

  const getRelatedItems = (itemId: number): number[] => {
    const currentItem = timelineData.find((item) => item.id === itemId)
    return currentItem ? currentItem.relatedIds : []
  }

  const isRelatedToActive = (itemId: number): boolean => {
    if (!activeNodeId) return false
    return getRelatedItems(activeNodeId).includes(itemId)
  }

  if (!isMounted) {
    return <div className='h-[50vh] md:h-[95dvh] 2xl:h-[70vh]'></div>
  }

  return (
    <div
      className='w-full flex flex-col items-center justify-center overflow-hidden bg-background'
      ref={containerRef}
      onClick={handleContainerClick}
    >
      <div className={'relative w-full max-w-[90vw] z-10 h-[50vh] md:h-[95dvh] 2xl:h-[80vh]'}>
        <div className='absolute w-full h-full flex items-center justify-center' ref={orbitRef}>
          <Globe />
          {timelineData.map((item, index) => {
            const position = calculateNodePosition(index, timelineData.length)
            const isExpanded = expandedItems[item.id]
            const isRelated = isRelatedToActive(item.id)

            const nodeStyle = {
              transform: `translate(${position.x}px, ${position.y}px)`,
              zIndex: isExpanded ? 200 : position.zIndex
            }

            return (
              <div
                key={item.id}
                ref={(el) => (nodeRefs.current[item.id] = el) as any}
                className='absolute transition-all duration-500 cursor-pointer flex items-center justify-center'
                style={nodeStyle}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleItem(item.id)
                }}
              >
                <div
                  className={cn(
                    'w-16 h-16 rounded-full mx-auto flex items-center justify-center transition-all duration-300',
                    'max-sm:w-14 max-sm:h-14 md:w-18 md:h-18',
                    {
                      'bg-card text-foreground border-border border-2 shadow-lg shadow-accent/20 dark:shadow-accent/20 scale-150':
                        isExpanded,
                      'bg-card/50 text-foreground border-border border-2': isRelated && !isExpanded,
                      'bg-secondary text-secondary-foreground': !isExpanded && !isRelated
                    }
                  )}
                >
                  <Image
                    width={36}
                    height={36}
                    alt={i18n._(item.title)}
                    className='max-sm:w-2/3'
                    src={item.icon}
                  />
                </div>
                <div
                  className={cn(
                    'absolute whitespace-nowrap font-semibold tracking-wider max-sm:top-15 top-[85px] text-xs sm:text-sm transition-all duration-300',
                    isExpanded ? 'hidden' : 'text-muted-foreground'
                  )}
                >
                  {i18n._(item.title)}
                </div>
                {isExpanded && (
                  <Card
                    className={cn(
                      'absolute rounded-xl left-1/2 -translate-x-1/2 top-20 w-64',
                      'bg-card/90 backdrop-blur-lg border-border/30 shadow-xl shadow-accent/10 overflow-visible'
                    )}
                  >
                    <CardHeader className={cn('p-4 pb-2')}>
                      <CardTitle className={cn('text-foreground max-sm:text-base text-base')}>
                        {i18n._(item.title)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className={cn('!pt-0', 'p-4 text-xs')}>
                      <p className='text-muted-foreground'>{i18n._(item.content)}</p>
                      {item.link && (
                        <a
                          href={item.link}
                          target='_blank'
                          rel='noopener noreferrer'
                          className={cn(
                            'inline-flex items-center font-medium mt-2',
                            'text-xs',
                            'text-orange-500 hover:text-orange-600 transition-colors'
                          )}
                        >
                          {i18n._(`Read more`)}
                          <ArrowRight size={12} className='ml-1' />
                        </a>
                      )}
                      {item.relatedIds.length > 0 && (
                        <div className='mt-2 pt-2 border-t border-border/10'>
                          <div className='flex items-center mb-1'>
                            <Link size={10} className='text-muted-foreground mr-1' />
                            <h4
                              className={cn(
                                'uppercase tracking-wider font-medium text-muted-foreground text-xs'
                              )}
                            >
                              {i18n._(`Related ecosystem`)}
                            </h4>
                          </div>
                          <div className='flex flex-wrap gap-2 pt-1'>
                            {item.relatedIds.map((relatedId: number) => {
                              const relatedItem = timelineData.find((i) => i.id === relatedId)
                              return (
                                <Button
                                  key={relatedId}
                                  variant='outline'
                                  size='sm'
                                  className={cn(
                                    'flex items-center px-2 py-0 border-border/20 bg-transparent hover:bg-accent/10 transition-all h-6 text-xs',
                                    'rounded-none text-muted-foreground hover:text-foreground dark:hover:text-foreground'
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleItem(relatedId)
                                  }}
                                >
                                  {i18n._(relatedItem?.title)}
                                  <ArrowRight size={8} className='ml-1 text-orange-400' />
                                </Button>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
